import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatsPeopleList, type PeopleKind } from "@/components/stats/StatsPeopleList";
import { api } from "@/lib/api";
import { TODAY_CHANGED_EVENT } from "@/lib/pointage";
import { cn } from "@/lib/utils";
import { SETTINGS_DEFAULTS } from "@shared/schema";
import { parisDateOf } from "@shared/followup";
import type { CallStatsBlock, CallStatsResponse } from "@shared/call-stats";

const MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const pad = (n: number) => String(n).padStart(2, "0");
const ym = (y: number, m: number) => `${y}-${pad(m)}`; // m : 1-12
const dm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const pct = (r: number | null) => (r == null ? "–" : `${Math.round(r * 100)} %`);

function monthBounds(y: number, m: number) {
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${ym(y, m)}-01`, to: `${ym(y, m)}-${pad(last)}` };
}

const KIND_LABEL: Record<PeopleKind, string> = {
  presents: "Présents",
  absents: "Absents",
  aPointer: "À pointer",
};

type Selected = { row: number | "total"; kind: PeopleKind };

export function StatsPage() {
  const [params, setParams] = useSearchParams();
  const todayIso = parisDateOf(new Date().toISOString()) ?? new Date().toISOString().slice(0, 10);
  const curY = Number(todayIso.slice(0, 4));
  const curM = Number(todayIso.slice(5, 7));
  const curKey = ym(curY, curM);

  const raw = params.get("mois");
  const valid = !!raw && /^\d{4}-(0[1-9]|1[0-2])$/.test(raw) && raw <= curKey;
  const key = valid ? raw! : curKey;
  const y = Number(key.slice(0, 4));
  const m = Number(key.slice(5, 7));

  useEffect(() => {
    if (!valid) setParams({ mois: curKey }, { replace: true });
  }, [valid, curKey, setParams]);

  const [data, setData] = useState<CallStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Selected | null>(null);
  const [bookingUrl, setBookingUrl] = useState<string>(SETTINGS_DEFAULTS.calendly_booking_url);

  useEffect(() => {
    let active = true;
    api
      .settings()
      .then((r) => {
        const url = r.settings.calendly_booking_url;
        if (active && url) setBookingUrl(url);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    const { from, to } = monthBounds(y, m);
    return api
      .callStats({ from, to, group: "week" })
      .then((d) => {
        if (id !== reqId.current) return;
        setData(d);
        setError(null);
      })
      .catch((e) => {
        if (id !== reqId.current) return;
        setError(e instanceof Error ? e.message : "Erreur");
      });
  }, [y, m]);

  useEffect(() => {
    setData(null);
    setError(null);
    setSelected(null);
    void load();
    return () => {
      reqId.current++;
    };
  }, [load]);

  useEffect(() => {
    const h = () => void load();
    window.addEventListener(TODAY_CHANGED_EVENT, h);
    return () => window.removeEventListener(TODAY_CHANGED_EVENT, h);
  }, [load]);

  const go = (delta: number) => {
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setParams({ mois: ym(d.getUTCFullYear(), d.getUTCMonth() + 1) });
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const weeks = (data.weeks ?? []).map((b, i) => ({
      id: i as number | "total",
      label: `Sem. du ${dm(b.from)}`,
      title: `semaine du ${dm(b.from)}`,
      block: b,
      total: false,
    }));
    return [
      ...weeks,
      { id: "total" as number | "total", label: "Total", title: MONTHS[m - 1], block: data.total, total: true },
    ];
  }, [data, m]);

  const showAPointer = (data?.total.counts.aPointer ?? 0) > 0;
  const selRow = selected ? rows.find((r) => r.id === selected.row) : undefined;
  const selCount = selected && selRow ? selRow.block.counts[selected.kind] : null;
  useEffect(() => {
    if (selCount === 0) setSelected(null);
  }, [selCount]);
  // Colonne « à pointer » masquée : la sélection n'a plus d'objet.
  useEffect(() => {
    if (!showAPointer) setSelected((s) => (s?.kind === "aPointer" ? null : s));
  }, [showAPointer]);

  const toggle = (row: number | "total", kind: PeopleKind) =>
    setSelected((s) => (s && s.row === row && s.kind === kind ? null : { row, kind }));

  const cell = (row: number | "total", kind: PeopleKind, b: CallStatsBlock, strong: boolean) => {
    const n = b.counts[kind];
    if (n === 0) return <span className={strong ? "font-bold" : ""}>0</span>;
    const on = selected?.row === row && selected.kind === kind;
    return (
      <button
        type="button"
        onClick={() => toggle(row, kind)}
        aria-pressed={on}
        className={cn(
          "min-w-8 rounded-md px-2 py-1 font-semibold tabular-nums underline decoration-dotted underline-offset-4 max-sm:min-h-11",
          on
            ? "bg-[var(--color-navy-700)] text-white"
            : "text-[var(--color-navy-700)] hover:bg-[var(--color-navy-50)]",
        )}
      >
        {n}
      </button>
    );
  };

  const heads = ["RDV", "Présents", "Absents", ...(showAPointer ? ["À pointer"] : []), "Présence", "Absents repris", "Closés", "Closing"];

  return (
    <>
      <PageHeader
        title="Statistiques"
        subtitle="Présence et closing par semaine"
        actions={
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Mois précédent"
              onClick={() => go(-1)}
              className="grid h-11 w-11 place-items-center rounded-lg hover:bg-[var(--color-navy-50)] sm:h-9 sm:w-9"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="min-w-36 text-center text-sm font-semibold capitalize text-[var(--color-navy-700)]">
              {MONTHS[m - 1]} {y}
            </span>
            <button
              type="button"
              aria-label="Mois suivant"
              disabled={key >= curKey}
              onClick={() => go(1)}
              className="grid h-11 w-11 place-items-center rounded-lg hover:bg-[var(--color-navy-50)] disabled:opacity-30 disabled:hover:bg-transparent sm:h-9 sm:w-9"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        }
      />

      {error ? (
        <div className="surface p-6">
          <p className="text-[var(--color-coral-600)]">{error}</p>
        </div>
      ) : !data ? (
        <div className="min-h-64 grid place-items-center">
          <div className="h-8 w-8 rounded-full border-2 border-[var(--color-navy-600)] border-r-transparent animate-spin" />
        </div>
      ) : (
        <>
          <div className="surface overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--color-muted-foreground)]">
                  <th className="sticky left-0 z-10 bg-white px-4 py-3 font-semibold capitalize">
                    {MONTHS[m - 1]}
                  </th>
                  {heads.map((h) => (
                    <th key={h} className="whitespace-nowrap px-3 py-3 text-right font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const c = r.block.counts;
                  const low = c.presenceRate != null && c.presenceRate < 0.6;
                  const bold = r.total ? "font-bold" : "";
                  return (
                    <tr
                      key={String(r.id)}
                      className={cn(
                        "border-t border-[var(--color-border)] tabular-nums",
                        r.total && "bg-[var(--color-navy-50)]",
                      )}
                    >
                      <td
                        className={cn(
                          "sticky left-0 z-10 whitespace-nowrap px-4 py-2.5",
                          r.total ? "bg-[var(--color-navy-50)] font-bold" : "bg-white",
                        )}
                      >
                        {r.label}
                      </td>
                      <td className={cn("px-3 py-2.5 text-right", bold)}>{c.rdv}</td>
                      <td className={cn("px-3 py-2.5 text-right", bold)}>{cell(r.id, "presents", r.block, r.total)}</td>
                      <td className={cn("px-3 py-2.5 text-right", bold)}>{cell(r.id, "absents", r.block, r.total)}</td>
                      {showAPointer && (
                        <td className={cn("px-3 py-2.5 text-right", bold)}>{cell(r.id, "aPointer", r.block, r.total)}</td>
                      )}
                      <td
                        className={cn(
                          "px-3 py-2.5 text-right",
                          bold,
                          low && "font-bold text-[var(--color-coral-600)]",
                        )}
                      >
                        {pct(c.presenceRate)}
                      </td>
                      <td className={cn("px-3 py-2.5 text-right", bold)}>{c.absentsRepris}</td>
                      <td className={cn("px-3 py-2.5 text-right", bold)}>{c.closes}</td>
                      <td className={cn("px-3 py-2.5 text-right", bold)}>{pct(c.closingRate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {selected && selRow && (
            <section className="surface mt-4 p-4">
              <h2 className="mb-1 text-sm font-semibold text-[var(--color-navy-700)]">
                {KIND_LABEL[selected.kind]} · {selRow.title}
              </h2>
              <StatsPeopleList
                kind={selected.kind}
                entries={selRow.block[selected.kind]}
                bookingUrl={bookingUrl}
              />
            </section>
          )}
        </>
      )}
    </>
  );
}
